interface ContactReviewData {
    assignmentId: number;
    contactName: string;
    contactTitle: string;
    contactEmail?: string;
    companyName: string;
    companyDomain?: string;
    companyDescription?: string;
    snippets: Array<{ key: string; prompt: string; value: string }>;
    sequenceSteps: Array<{ index: number; subject?: string; body: string }>;
    researchSignals: Array<{ name: string; qualified: boolean; summary?: string }>;
}

interface RefineReplyContext {
    assignmentId: number;
    draftBody: string;
    contactName?: string;
    contactTitle?: string;
    companyName?: string;
}

interface CampaignChatSummary {
    id: string;
    messagesCount: number;
}

interface GlobalChatContext {
    page: 'automatic-experiment' | 'new-campaign' | 'campaign-editor' | 'review' | 'portfolio-creative-bets' | 'contact-review' | 'improve-campaign' | 'optimize-hit-ratio' | 'review-targeting' | 'refine-reply' | 'default';
    icpId?: number;
    intent?: 'add-must-have-filter' | 'add-enrichment';
    contactReview?: ContactReviewData;
    refineReply?: RefineReplyContext;
    initialPrompt?: string;
    prefillPrompt?: string;
    greeting?: string;
}

export function useGlobalChat() {
    const isOpen = useState<boolean>('global-chat-open', () => false);
    const chatId = useState<string | undefined>('global-chat-id', () => undefined);
    const chatContext = useState<GlobalChatContext>('global-chat-context', () => ({ page: 'default' }));
    const isStreaming = useState<boolean>('global-chat-streaming', () => false);
    const isDraftChat = useState<boolean>('global-chat-is-draft-chat', () => false);
    const chatTransitionVersion = useState<number>('global-chat-transition-version', () => 0);

    // Auto-close sidebar when navigating away from campaign pages,
    // but keep it open while the AI is actively streaming a response
    const route = useRoute();
    const chatAllowedPaths = ['/campaigns', '/replies', '/contacts', '/activity'];
    watch(() => route.path, (path) => {
        if (isOpen.value && !chatAllowedPaths.some(p => path.startsWith(p)) && !isStreaming.value) {
            isOpen.value = false;
        }
    });

    function openChat(context?: Partial<GlobalChatContext>) {
        if (context) {
            chatContext.value = { ...chatContext.value, ...context };
        }
        isOpen.value = true;
    }

    function closeChat() {
        isOpen.value = false;
    }

    function toggleChat() {
        isOpen.value = !isOpen.value;
    }

    function startNewChat(context: Partial<GlobalChatContext> = {}) {
        chatTransitionVersion.value++;
        chatId.value = generateUUID();
        chatContext.value = { page: 'default', ...context };
        isDraftChat.value = true;
    }

    function ensureChatId(): string {
        if (isValidUUID(chatId.value)) return chatId.value;
        const id = generateUUID();
        chatId.value = id;
        isDraftChat.value = true;
        return id;
    }

    function switchChat(id: string, context?: Partial<GlobalChatContext>) {
        chatTransitionVersion.value++;
        if (context) {
            chatContext.value = { ...chatContext.value, ...context };
        }
        const validId = isValidUUID(id);
        chatId.value = validId ? id : generateUUID();
        isDraftChat.value = !validId;
    }

    function useDraftChat(id: string, context?: Partial<GlobalChatContext>) {
        chatTransitionVersion.value++;
        if (context) {
            chatContext.value = { ...chatContext.value, ...context };
        }
        chatId.value = isValidUUID(id) ? id : generateUUID();
        isDraftChat.value = true;
    }

    function openChatWithIntent(intent: GlobalChatContext['intent'], icpId?: number) {
        chatTransitionVersion.value++;
        chatContext.value = {
            page: 'campaign-editor',
            icpId,
            intent,
        };
        chatId.value = generateUUID();
        isDraftChat.value = true;
        isOpen.value = true;
    }

    async function restoreLatestChatForCampaign(icpId: number) {
        const previousIcpId = chatContext.value.icpId;
        const transitionVersion = ++chatTransitionVersion.value;
        chatContext.value = { page: 'campaign-editor', icpId };

        if (previousIcpId === icpId && isValidUUID(chatId.value)) return;

        chatId.value = generateUUID();
        isDraftChat.value = true;

        try {
            const chats = await $fetch<CampaignChatSummary[]>('/api/chats', { query: { icpId } });
            if (chatTransitionVersion.value !== transitionVersion || chatContext.value.icpId !== icpId) return;

            const recentChat = chats.find((chat) => chat.messagesCount > 0);
            if (recentChat) {
                switchChat(recentChat.id);
            }
        } catch {
            // Keep the campaign-scoped draft chat when conversation preloading fails.
        }
    }

    function onChatMaximize() {
        const id = ensureChatId();
        const query: Record<string, string> = {};
        if (chatContext.value.page !== 'default') {
            query.page = chatContext.value.page;
        }
        if (chatContext.value.icpId) {
            query.icpId = String(chatContext.value.icpId);
        }
        if (chatContext.value.initialPrompt) {
            query.prompt = chatContext.value.initialPrompt;
        }
        if (chatContext.value.intent) {
            query.intent = chatContext.value.intent;
        }
        if (isDraftChat.value) {
            query.draft = '1';
        }

        isOpen.value = false;
        navigateTo({ path: `/chat/${id}`, query });
    }

    function startAutomaticExperimentChat() {
        startNewChat({
            page: 'automatic-experiment',
            initialPrompt: 'Generate Experiments. I authorize preparing a capacity-sized batch of new perceived-value campaigns now, without selecting individual bets or approving each plan. Choose the best audience and narration for each distinct new value. Use the automatic-experiment preset and save the group and campaign strategies. Keep every campaign on manual contact approval; do not approve contacts, enable autopilot, send outreach, or modify existing campaigns.',
        });
        onChatMaximize();
    }

    function onChatCampaignCreated(campaignId: number) {
        chatContext.value = { ...chatContext.value, icpId: campaignId };
    }

    async function onChatCampaignLaunched(campaignId: number) {
        isOpen.value = false;
        await navigateTo(await getPostLaunchCampaignRoute(campaignId));
    }

    return {
        isOpen,
        chatId,
        chatContext,
        isStreaming,
        isDraftChat,
        openChat,
        closeChat,
        toggleChat,
        startNewChat,
        startAutomaticExperimentChat,
        switchChat,
        useDraftChat,
        openChatWithIntent,
        restoreLatestChatForCampaign,
        onChatMaximize,
        onChatCampaignCreated,
        onChatCampaignLaunched,
    };
}
